import React, { useState, useEffect, useRef, useCallback, ChangeEvent, FormEvent } from "react";
import { getLocations, createLocation, updateLocation, deleteLocation } from "./api";
import { Location } from "./types";
import detailMessage from "./detailMessage";
import { useLang } from "./LangContext";
import "./LocationManager.css";

interface LocationFormData {
  name: string;
  address: string;
  is_default: boolean;
}

interface Props {
  token: string;
  onLocationsChange?: (locations: Location[]) => void;
}

export default function LocationManager({ token, onLocationsChange }: Props) {
  const { t } = useLang();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<LocationFormData>({ name: "", address: "", is_default: false });
  const [showForm, setShowForm] = useState(false);

  // Hold the latest callback in a ref so refresh() doesn't change identity when
  // the parent re-renders — otherwise the effect below loops forever.
  const onLocationsChangeRef = useRef(onLocationsChange);
  useEffect(() => {
    onLocationsChangeRef.current = onLocationsChange;
  }, [onLocationsChange]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLocations(token);
      setLocations(data);
      if (onLocationsChangeRef.current) onLocationsChangeRef.current(data);
    } catch {
      setError(t.failedLoadLocations);
    } finally {
      setLoading(false);
    }
  }, [token, t.failedLoadLocations]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const startCreate = () => {
    setEditingId(null);
    setForm({ name: "", address: "", is_default: locations.length === 0 });
    setShowForm(true);
    setError("");
  };

  const startEdit = (loc: Location) => {
    setEditingId(loc.id);
    setForm({ name: loc.name, address: loc.address ?? "", is_default: loc.is_default });
    setShowForm(true);
    setError("");
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setError("");
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (editingId != null) {
        await updateLocation(editingId, form, token);
      } else {
        await createLocation(form, token);
      }
      cancelForm();
      await refresh();
    } catch (err) {
      setError(detailMessage(err, t.failedSaveLocation));
    } finally {
      setBusy(false);
    }
  };

  const handleSetDefault = async (loc: Location) => {
    setBusy(true);
    setError("");
    try {
      await updateLocation(loc.id, { is_default: true }, token);
      await refresh();
    } catch {
      setError(t.failedSetDefaultLocation);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (loc: Location) => {
    if (!window.confirm(t.deleteLocationConfirm(loc.name))) return;
    setBusy(true);
    setError("");
    try {
      await deleteLocation(loc.id, token);
      await refresh();
    } catch {
      setError(t.failedDeleteLocation);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="location-manager">
      <div className="location-manager-header">
        <h4>{t.locationsHeading}</h4>
        {!showForm && (
          <button type="button" className="submit-button" onClick={startCreate} disabled={busy}>
            {t.addLocationButton}
          </button>
        )}
      </div>
      <p className="duration-options-hint">
        {t.locationDefaultHint}
      </p>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="location-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="loc-name">{t.locationNameLabel}</label>
            <input
              id="loc-name"
              type="text"
              value={form.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
              maxLength={80}
              required
              disabled={busy}
            />
          </div>
          <div className="form-group">
            <label htmlFor="loc-address">{t.locationAddressLabel}</label>
            <input
              id="loc-address"
              type="text"
              value={form.address}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, address: e.target.value })}
              maxLength={240}
              required
              disabled={busy}
            />
          </div>
          <label className="location-default-row">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, is_default: e.target.checked })}
              disabled={busy}
            />
            {t.locationUseAsDefault}
          </label>
          <div className="form-actions">
            <button type="submit" className="submit-button" disabled={busy}>
              {busy ? t.savingButton : editingId != null ? t.updateButton : t.createButton}
            </button>
            <button type="button" className="button button-secondary" onClick={cancelForm} disabled={busy}>
              {t.cancelButton}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="vacation-loading">{t.loadingLocations}</div>
      ) : locations.length === 0 ? (
        <p className="no-vacations">{t.noLocationsYet}</p>
      ) : (
        <ul className="location-list">
          {locations.map((loc) => (
            <li key={loc.id} className={`location-item${loc.is_default ? " default" : ""}`}>
              <div className="location-content">
                <div className="location-name-row">
                  <strong>{loc.name}</strong>
                  {loc.is_default && <span className="status-badge active">{t.defaultBadge}</span>}
                </div>
                <p className="location-address">{loc.address}</p>
              </div>
              <div className="location-actions">
                {!loc.is_default && (
                  <button
                    type="button"
                    className="button button-sm button-outline"
                    onClick={() => handleSetDefault(loc)}
                    disabled={busy}
                  >
                    {t.makeDefaultButton}
                  </button>
                )}
                <button
                  type="button"
                  className="button button-sm button-outline"
                  onClick={() => startEdit(loc)}
                  disabled={busy}
                >
                  {t.editButton}
                </button>
                <button
                  type="button"
                  className="button button-sm button-danger"
                  onClick={() => handleDelete(loc)}
                  disabled={busy}
                >
                  {t.deleteButton}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
